import { PhotoProvider } from "react-photo-view"

import { FileViewer } from "components/fileviewer/FileViewer"

import { useTimezone } from "hooks/useTimezone"

import { KycFilesWithSignedImages } from "services/kyc/type"

import { ReactComponent as DocIcon } from "../../assets/icons/doc.svg"

interface UserKycTableProps {
  kycData: KycFilesWithSignedImages | null
}

const fieldLabels = [
  { key: "img_idcard_front", label: "ID Card (Front)" },
  { key: "img_idcard_back", label: "ID Card (Back)" },
  { key: "doc_proof_address", label: "Proof of Address" },
  { key: "doc_add_verify", label: "Additional Verification" }
] as const

export const UserKycTable = ({ kycData }: UserKycTableProps) => {
  const { formatDateTime } = useTimezone()
  const kyc_files = kycData ?? null

  const isOtherObject = typeof kyc_files?.other === "object" && kyc_files.other !== null && "reason" in kyc_files.other

  return (
    <PhotoProvider>
      <div className="w-full rounded-lg border border-[rgba(0,0,0,0.25)] dark:border-[rgba(255,255,255,0.15)] overflow-x-auto">
        <table className="table table-compact w-full">
          <thead className="bg-[rgba(212,212,212,0.14)] dark:bg-[rgba(212,212,212,0.1)] text-left text-[rgba(0,0,0,0.7)] dark:text-[rgba(255,255,255,0.46)] text-sm">
            <tr>
              <th className="px-4 py-3">Document Type</th>
              <th className="px-4 py-3">File</th>
              <th className="px-4 py-3 text-right pr-12">Created At</th>
              <th className="px-4 py-3 text-right pr-12">Updated At</th>
            </tr>
          </thead>
          <tbody>
            {kyc_files ? (
              fieldLabels.map(({ key, label }) => {
                const value = kyc_files.kycSignedImages?.[key]
                const isValidString = typeof value === "string" && value.length > 0

                return (
                  <tr key={key}>
                    <td className="px-4 py-3">{label}</td>
                    <td className="px-4 py-3">
                      {isValidString ? (
                        <FileViewer title="" path={value} />
                      ) : (
                        <>
                          <span className="text-red italic">Missing</span>
                          <span> ❌</span>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right pr-8">{kyc_files.created_at ? formatDateTime(kyc_files.created_at) : "—"}</td>
                    <td className="px-4 py-3 text-right pr-8">{kyc_files.updated_at ? formatDateTime(kyc_files.updated_at) : "—"}</td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td className="px-4 py-16 text-center" colSpan={4}>
                  <div className="flex flex-col items-center justify-center gap-2 text-gray-600 dark:text-gray-400">
                    <DocIcon className="w-10 h-10" />
                    <span>No Data</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
          {kyc_files && (
            <tfoot className="bg-[rgba(212,212,212,0.05)] text-sm text-black dark:text-white">
              <tr>
                <td className="px-4 py-2 font-normal text-xl">Status</td>
                <td className="px-4 py-2 text-right" colSpan={3}>
                  <span
                    className={
                      kyc_files.status === "PENDING"
                        ? "text-orange-400 font-medium text-xl"
                        : kyc_files.status === "COMPLETED"
                        ? "text-green font-medium text-xl"
                        : kyc_files.status === "DECLINED"
                        ? "text-red font-medium text-xl"
                        : "text-gray-500 text-xl"
                    }
                  >
                    {kyc_files.status || "—"}
                  </span>
                </td>
              </tr>
              {isOtherObject && (
                <tr>
                  <td className="px-4 py-2 font-normal text-xl">Reason</td>
                  <td className="px-4 py-2 font-normal italic text-right" colSpan={3}>
                    {(kyc_files.other as { reason?: string }).reason || "—"}
                  </td>
                </tr>
              )}
              <tr>
                <td className="px-4 py-2 font-normal">Created At</td>
                <td className="px-4 py-2 font-normal text-right" colSpan={3}>
                  {kyc_files.created_at ? formatDateTime(kyc_files.created_at) : "—"}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 font-normal">Updated At</td>
                <td className="px-4 py-2 font-normal text-right" colSpan={3}>
                  {kyc_files.updated_at ? formatDateTime(kyc_files.updated_at) : "—"}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </PhotoProvider>
  )
}
